import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import TableColumnFilter from '../../components/TableColumnFilter';
import ActiveFilterChips, { FilterChip } from '../../components/ActiveFilterChips';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  BookOpenIcon,
  CheckBadgeIcon,
  PencilSquareIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon
} from '@heroicons/react/24/outline';

interface KnowledgeArticle {
  id?: string;
  _id?: string;
  number: string;
  shortDescription: string;
  title?: string;
  content?: string;
  category?: string;
  categoryName?: string;
  knowledgeBaseId?: string;
  KnowledgeBase?: {
    id: string;
    name: string;
  };
  kbBase?: {
    id: string;
    name: string;
  };
  tags?: string[];
  state: string;
  published: boolean;
  publishedDate?: string;
  version: number;
  views: number;
  rating: number;
  ratingCount?: number;
  assignedTo?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  author?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  authorId?: string;
  approvedBy?: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  relatedArticles?: string[];
  createdAt: string;
  updatedAt: string;
}

const stateColors: Record<string, string> = {
  new: 'bg-gray-100 text-gray-800',
  draft: 'bg-gray-100 text-gray-800',
  in_review: 'bg-yellow-100 text-yellow-800',
  review: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-blue-100 text-blue-800',
  published: 'bg-green-100 text-green-800',
  retired: 'bg-red-100 text-red-800',
  rejected: 'bg-red-100 text-red-800'
};

const stateLabels: Record<string, string> = {
  new: 'Draft',
  draft: 'Draft',
  in_review: 'Under Review',
  review: 'Under Review',
  approved: 'Approved',
  published: 'Published',
  retired: 'Retired',
  rejected: 'Rejected'
};

const stateOptions = [
  { value: 'new', label: 'Draft' },
  { value: 'in_review', label: 'Under Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'published', label: 'Published' },
  { value: 'retired', label: 'Retired' },
  { value: 'rejected', label: 'Rejected' },
];

// Hardcoded knowledge article categories
const KNOWLEDGE_CATEGORIES = [
  { value: 'guideline', label: 'Guideline' },
  { value: 'known_error', label: 'Known Error' },
  { value: 'kb_document', label: 'KB Document' },
  { value: 'domain_controller_build', label: 'Domain Controller Build' },
  { value: 'mfa_modification', label: 'MFA Modification' },
  { value: 'investigation_steps', label: 'Investigation steps' },
  { value: 'itsm_l2_sop', label: 'ITSM - L2 SOP For Attaching and Initiali' },
  { value: 'sending_encrypted_email', label: 'Sending Encrypted Email' },
  { value: 'windows_updates', label: 'Windows Updates' },
  { value: 'fsmo_migration', label: 'FSMO Migration' }
];

interface KnowledgeBase {
  id: string;
  name: string;
  active: boolean;
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

// Helper function to strip HTML tags and decode HTML entities
const stripHtml = (html: string): string => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

const KnowledgeList: React.FC = () => {
  const { formatDate } = useDateFormatter();
  const [searchParams, setSearchParams] = useSearchParams();

  const patchSearchParams = useCallback(
    (updates: Record<string, string | null | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, val] of Object.entries(updates)) {
            if (val === null || val === undefined || val === '') {
              next.delete(key);
            } else {
              next.set(key, val);
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const page = useMemo(() => {
    const p = parseInt(searchParams.get('page') || '1', 10);
    return Number.isFinite(p) && p >= 1 ? p : 1;
  }, [searchParams]);

  const pageSize = useMemo(() => {
    const ps = parseInt(searchParams.get('pageSize') || '25', 10);
    return PAGE_SIZE_OPTIONS.includes(ps) ? ps : 25;
  }, [searchParams]);

  const appliedSearchTerm = searchParams.get('search') ?? '';
  const filterNumber = searchParams.get('number') ?? '';
  const filterTitle = searchParams.get('title') ?? '';
  const filterStatus = useMemo(
    () => (searchParams.get('state') ?? '').split(',').filter(Boolean),
    [searchParams]
  );
  const filterKnowledgeBase = useMemo(
    () => (searchParams.get('knowledgeBaseId') ?? '').split(',').filter(Boolean),
    [searchParams]
  );
  const filterAuthor = searchParams.get('authorId') ?? '';
  const filterCategory = useMemo(
    () => (searchParams.get('category') ?? '').split(',').filter(Boolean),
    [searchParams]
  );

  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') ?? '');
  useEffect(() => {
    setSearchInput(appliedSearchTerm);
  }, [appliedSearchTerm]);

  useEffect(() => {
    const id = setTimeout(() => {
      const trimmed = searchInput.trim();
      if (trimmed === appliedSearchTerm) return;
      patchSearchParams({ search: trimmed || null, page: '1' });
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput, appliedSearchTerm, patchSearchParams]);

  // Fetch knowledge bases
  const { data: knowledgeBasesData } = useQuery({
    queryKey: ['knowledge-bases-filter'],
    queryFn: async () => {
      const response = await api.get('/knowledge-base?limit=100');
      return response.data;
    }
  });

  // Fetch users for author filter
  const { data: usersData } = useQuery({
    queryKey: ['users-filter'],
    queryFn: async () => {
      const response = await api.get('/users?limit=200&role=agent,manager,admin,super_admin');
      return response.data;
    }
  });

  const knowledgeBases: KnowledgeBase[] = knowledgeBasesData?.knowledgeBases || [];
  const users: User[] = usersData?.users || [];

  const knowledgeBaseOptions = useMemo(
    () => knowledgeBases.map((kb) => ({ value: kb.id, label: kb.name })),
    [knowledgeBases]
  );

  const authorOptions = useMemo(
    () =>
      users.map((u) => ({
        value: u.id,
        label: `${u.firstName} ${u.lastName}`.trim() || u.email,
      })),
    [users]
  );

  const renderColumnHeader = useCallback(
    (columnKey: string, columnLabel: string) => {
      switch (columnKey) {
        case 'number':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterNumber}
              placeholder="Filter by number..."
              onChange={(v) => {
                patchSearchParams({ number: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'title':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterTitle}
              placeholder="Filter by title..."
              onChange={(v) => {
                patchSearchParams({ title: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'knowledgeBase':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="multi-select"
              options={knowledgeBaseOptions}
              value={filterKnowledgeBase}
              placeholder="All Knowledge Bases"
              onChange={(v) => {
                const ids = Array.isArray(v) ? v : [];
                patchSearchParams({
                  knowledgeBaseId: ids.length > 0 ? ids.join(',') : null,
                  page: '1',
                });
              }}
            />
          );
        case 'category':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="multi-select"
              options={KNOWLEDGE_CATEGORIES}
              value={filterCategory}
              placeholder="All Categories"
              onChange={(v) => {
                const cats = Array.isArray(v) ? v : [];
                patchSearchParams({
                  category: cats.length > 0 ? cats.join(',') : null,
                  page: '1',
                });
              }}
            />
          );
        case 'status':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="multi-select"
              options={stateOptions}
              value={filterStatus}
              placeholder="All Statuses"
              onChange={(v) => {
                const states = Array.isArray(v) ? v : [];
                patchSearchParams({
                  state: states.length > 0 ? states.join(',') : null,
                  page: '1',
                });
              }}
            />
          );
        case 'author':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="searchable-select"
              options={authorOptions}
              value={filterAuthor}
              placeholder="All Authors"
              onChange={(v) => {
                patchSearchParams({
                  authorId: typeof v === 'string' && v ? v : null,
                  page: '1',
                });
              }}
            />
          );
        default:
          return columnLabel;
      }
    },
    [
      filterNumber,
      filterTitle,
      filterKnowledgeBase,
      filterCategory,
      filterStatus,
      filterAuthor,
      knowledgeBaseOptions,
      authorOptions,
      patchSearchParams,
    ]
  );

  // Build active filter chips
  const activeFilterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];

    if (appliedSearchTerm) {
      chips.push({
        id: 'search',
        label: 'Search',
        value: appliedSearchTerm,
        color: 'gray',
      });
    }

    if (filterNumber) {
      chips.push({ id: 'number', label: 'Number', value: filterNumber, color: 'blue' });
    }

    if (filterTitle) {
      chips.push({ id: 'title', label: 'Title', value: filterTitle, color: 'blue' });
    }

    if (filterStatus.length > 0) {
      const statusLabels = filterStatus.map(s => stateLabels[s] || s);
      chips.push({
        id: 'status',
        label: 'Status',
        value: statusLabels.join(', '),
        color: 'blue',
      });
    }

    if (filterKnowledgeBase.length > 0) {
      const kbNames = filterKnowledgeBase.map(kbId => {
        const kb = knowledgeBases.find(k => k.id === kbId);
        return kb?.name || 'Unknown';
      });
      chips.push({
        id: 'knowledgeBase',
        label: 'Knowledge Base',
        value: kbNames.join(', '),
        color: 'purple',
      });
    }

    if (filterAuthor) {
      const author = users.find(u => u.id === filterAuthor);
      chips.push({
        id: 'author',
        label: 'Author',
        value: author ? `${author.firstName} ${author.lastName}`.trim() : 'Unknown',
        color: 'green',
      });
    }

    if (filterCategory.length > 0) {
      const catNames = filterCategory.map(catValue => {
        const cat = KNOWLEDGE_CATEGORIES.find(c => c.value === catValue);
        return cat?.label || 'Unknown';
      });
      chips.push({
        id: 'category',
        label: 'Category',
        value: catNames.join(', '),
        color: 'purple',
      });
    }

    return chips;
  }, [appliedSearchTerm, filterNumber, filterTitle, filterStatus, filterKnowledgeBase, filterAuthor, filterCategory, knowledgeBases, users]);

  const handleRemoveFilterChip = (chipId: string) => {
    switch (chipId) {
      case 'search':
        setSearchInput('');
        patchSearchParams({ search: null, page: '1' });
        break;
      case 'number':
        patchSearchParams({ number: null, page: '1' });
        break;
      case 'title':
        patchSearchParams({ title: null, page: '1' });
        break;
      case 'status':
        patchSearchParams({ state: null, page: '1' });
        break;
      case 'knowledgeBase':
        patchSearchParams({ knowledgeBaseId: null, page: '1' });
        break;
      case 'author':
        patchSearchParams({ authorId: null, page: '1' });
        break;
      case 'category':
        patchSearchParams({ category: null, page: '1' });
        break;
    }
  };

  const handleClearAllFilters = () => {
    setSearchInput('');
    patchSearchParams({
      search: null,
      number: null,
      title: null,
      state: null,
      knowledgeBaseId: null,
      authorId: null,
      category: null,
      page: '1',
    });
  };

  // Fetch articles
  const { data, isLoading, error } = useQuery({
    queryKey: ['knowledge', page, pageSize, appliedSearchTerm, filterNumber, filterTitle, filterStatus, filterKnowledgeBase, filterAuthor, filterCategory],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString(),
        ...(appliedSearchTerm && { search: appliedSearchTerm }),
        ...(filterNumber && { number: filterNumber }),
        ...(filterTitle && { title: filterTitle }),
        ...(filterStatus.length > 0 && { state: filterStatus.join(',') }),
        ...(filterKnowledgeBase.length > 0 && { knowledgeBaseId: filterKnowledgeBase.join(',') }),
        ...(filterAuthor && { authorId: filterAuthor }),
        ...(filterCategory.length > 0 && { category: filterCategory.join(',') })
      });

      const response = await api.get(`/knowledge?${params}`);
      return response.data;
    }
  });

  // Fetch stats
  const { data: statsData } = useQuery({
    queryKey: ['knowledge-stats', appliedSearchTerm, filterNumber, filterTitle, filterStatus, filterKnowledgeBase, filterAuthor, filterCategory],
    queryFn: async () => {
      const response = await api.get('/knowledge/stats');
      return response.data;
    }
  });

  // Helper function to get category name from value
  const getCategoryName = (article: KnowledgeArticle): string => {
    if (article.categoryName) return article.categoryName;
    if (!article.category) return '-';
    const category = KNOWLEDGE_CATEGORIES.find(c => c.value === article.category);
    return category?.label || article.category || '-';
  };

  // Get author name
  const getAuthorName = (article: KnowledgeArticle): string => {
    if (article.author) {
      return `${article.author.firstName || ''} ${article.author.lastName || ''}`.trim() || article.author.email || '-';
    }
    if (article.assignedTo) {
      return `${article.assignedTo.firstName || ''} ${article.assignedTo.lastName || ''}`.trim() || article.assignedTo.email || '-';
    }
    return '-';
  };

  // Get knowledge base name
  const getKnowledgeBaseName = (article: KnowledgeArticle): string => {
    if (article.KnowledgeBase?.name) return article.KnowledgeBase.name;
    if (article.kbBase?.name) return article.kbBase.name;
    return '-';
  };

  // Calculate pagination info
  const totalItems = data?.total || 0;
  const totalPages = data?.totalPages || Math.ceil(totalItems / pageSize) || 1;
  const startItem = totalItems > 0 ? (page - 1) * pageSize + 1 : 0;
  const endItem = Math.min(page * pageSize, totalItems);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages + 2) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (page > 3) {
        pages.push('...');
      }

      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (page < totalPages - 2) {
        pages.push('...');
      }

      pages.push(totalPages);
    }

    return pages;
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">Error loading knowledge articles. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 flex flex-col h-full min-h-0 overflow-hidden">
      {/* Compact Header Bar */}
      <div className="bg-white shadow rounded-lg mb-2 flex-shrink-0">
        <div className="px-4 py-2">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
            <h1 className="text-lg font-semibold text-gray-900 whitespace-nowrap">Knowledge Articles</h1>

            <div className="flex items-center gap-2">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search articles..."
                  className="pl-8 pr-3 py-1.5 w-64 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
              <Link
                to="/knowledge/new"
                className="inline-flex items-center px-3 py-1.5 text-sm border border-transparent rounded-md shadow-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <PlusIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
                New
              </Link>
            </div>
          </div>

          {/* Active Filters */}
          {activeFilterChips.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <ActiveFilterChips
                chips={activeFilterChips}
                onRemove={handleRemoveFilterChip}
                onClearAll={handleClearAllFilters}
              />
            </div>
          )}

          {/* Stats Summary Bar */}
          <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between text-sm">
            <div className="flex items-center gap-6">
              <span className="text-gray-600">
                Total: <span className="font-semibold text-gray-900">{statsData?.total ?? totalItems}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                <span className="text-gray-600">Published:</span>
                <span className="font-semibold text-gray-900">{statsData?.published ?? 0}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                <span className="text-gray-600">Drafts:</span>
                <span className="font-semibold text-gray-900">{statsData?.draft ?? 0}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                <span className="text-gray-600">In Review:</span>
                <span className="font-semibold text-gray-900">{statsData?.in_review ?? 0}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-red-400"></span>
                <span className="text-gray-600">Retired:</span>
                <span className="font-semibold text-gray-900">{statsData?.retired ?? 0}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden flex-1 min-h-0 flex flex-col">
        <div className="overflow-auto flex-1">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  {renderColumnHeader('number', 'Number')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  {renderColumnHeader('title', 'Title')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  {renderColumnHeader('knowledgeBase', 'Knowledge Base')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  {renderColumnHeader('category', 'Category')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  {renderColumnHeader('status', 'Status')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  {renderColumnHeader('author', 'Author')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  Created
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data?.articles && data.articles.length > 0 ? (
                data.articles.map((article: KnowledgeArticle) => (
                  <tr key={article.id || article._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <Link
                        to={`/knowledge/${article.id || article._id}`}
                        className="text-indigo-600 hover:text-indigo-900 font-medium"
                      >
                        {article.number}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/knowledge/${article.id || article._id}`}
                          className="max-w-md truncate hover:text-indigo-600"
                        >
                          {stripHtml(article.title || article.shortDescription)}
                        </Link>
                        {article.published ? (
                          <CheckBadgeIcon className="h-4 w-4 text-green-500 flex-shrink-0" title="Published" />
                        ) : (
                          <PencilSquareIcon className="h-4 w-4 text-gray-400 flex-shrink-0" title="Draft" />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {getKnowledgeBaseName(article)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {getCategoryName(article)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${stateColors[article.state] || 'bg-gray-100 text-gray-800'}`}>
                        {stateLabels[article.state] || article.state}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {getAuthorName(article)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(article.createdAt)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <BookOpenIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-gray-500">No knowledge articles found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalItems > 0 && (
          <div className="px-4 py-3 border-t border-gray-200 bg-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700">Show</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  patchSearchParams({ pageSize: e.target.value, page: '1' });
                }}
                className="px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <span className="text-sm text-gray-700">entries</span>
            </div>

            <div className="text-sm text-gray-700">
              Showing {startItem} to {endItem} of {totalItems} entries
            </div>

            <div className="flex items-center gap-1">
              {/* First page */}
              <button
                onClick={() => patchSearchParams({ page: '1' })}
                disabled={page === 1}
                className="px-2 py-1 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="First page"
              >
                <ChevronDoubleLeftIcon className="h-4 w-4" />
              </button>

              {/* Previous */}
              <button
                onClick={() => patchSearchParams({ page: String(page - 1) })}
                disabled={page === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              {/* Page numbers */}
              {getPageNumbers().map((pageNum, index) => (
                pageNum === '...' ? (
                  <span key={`ellipsis-${index}`} className="px-2 py-1 text-sm text-gray-500">...</span>
                ) : (
                  <button
                    key={pageNum}
                    onClick={() => patchSearchParams({ page: String(pageNum) })}
                    className={`px-3 py-1 text-sm border rounded-md ${
                      page === pageNum
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                )
              ))}

              {/* Next */}
              <button
                onClick={() => patchSearchParams({ page: String(page + 1) })}
                disabled={page >= totalPages}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>

              {/* Last page */}
              <button
                onClick={() => patchSearchParams({ page: String(totalPages) })}
                disabled={page >= totalPages}
                className="px-2 py-1 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Last page"
              >
                <ChevronDoubleRightIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KnowledgeList;
